import React, { useState, useEffect } from "react";
import qs from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Language } from "../../../utils";
import { user as userActions } from "../../../actions";
import { Button } from "../../../components";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import makeClasses from "../styles";
import clsx from "clsx";
import { InputLabel } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

const StepTwo = ({ ci, name, categoryId, nextStep, history }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);

  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cities, setCities] = useState([]);

  const [inputCountry, setInputCountry] = useState(null);
  const [inputDepartment, setInputDepartment] = useState(null);
  const [inputCity, setInputCity] = useState(null);
  const [inputAddressStreet, setInputAddressStreet] = useState("");
  const [inputAddressNumber, setInputAddressNumber] = useState("");
  const [inputAddressApartment, setInputAddressApartment] = useState("");
  const [inputAddressCorner, setInputAddressCorner] = useState("");

  const canMoveNext =
    inputCountry !== null &&
    inputDepartment !== null &&
    inputCity !== null &&
    inputAddressStreet.trim().length > 0 &&
    inputAddressNumber.trim().length > 0 &&
    inputAddressCorner.trim().length > 0;

  useEffect(() => {
    dispatch(
      userActions.getCountries((countries) => {
        setCountries(countries);
        countries.forEach((country) => {
          if (country.alphaCode2 === "UY") {
            setInputCountry(country.id);
          }
        });
      }),
    );
  }, []);

  useEffect(() => {
    if (inputCountry) {
      setInputDepartment(null);
      setInputCity(null);
      setDepartments([]);
      setCities([]);
      dispatch(
        userActions.getDepartmentsByCountry(inputCountry, (departments) => {
          setDepartments(departments);
        }),
      );
    }
  }, [inputCountry]);

  useEffect(() => {
    if (inputDepartment) {
      setInputCity(null);
      setCities([]);
      dispatch(
        userActions.getCitiesByDepartments(inputDepartment, (cities) => {
          setCities(cities);
        }),
      );
    }
  }, [inputDepartment]);

  const getOptionLabel = (array) => (option) => {
    let found = null;
    if (array === "countries") found = countries.find((c) => c.id === option);
    else if (array === "departments")
      found = departments.find((d) => d.id === option);
    else if (array === "cities") found = cities.find((c) => c.id === option);
    return found ? found.name : "";
  };

  const handleSubmit = () => {
    const country = countries.find((c) => c.id === inputCountry);
    const city = cities.find((c) => c.id === inputCity);

    dispatch(
      userActions.startAffiliation(
        {
          countryISO: country.alphaCode2,
          documentTypeKey: "CI",
          document: ci,
          categoryId: categoryId,
          activityName: name,
          address: {
            cityId: city.id,
            street: inputAddressStreet.trim(),
            corner: inputAddressCorner.trim(),
            apartment: inputAddressApartment.trim(),
            postalCode: null,
            number: inputAddressNumber.trim(),
            description: null,
            longitude: 0,
            latitude: 0,
          },
          affiliationCode: qs.parse(history?.location?.search)?.code ?? null,
        },
        (affiliationResult, error) => {
          if (error) {
            toast.error(i18n.get("Register.RegisterErrorGeneric"), {
              autoClose: 5000,
            });
            return;
          }
          if (affiliationResult.traceId) {
            nextStep({ traceId: affiliationResult.traceId });
          }
        },
      ),
    );
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={classes.formTitleContainer}>
          <div className={classes.formTitle}>
            {i18n.get("Register.StepFormTitle")}
          </div>
          <div className={classes.formText}>
            {i18n.get("Register.Step2AddressFormText")}
          </div>
        </div>
        <form
          layout="vertical"
          onSubmit={handleSubmit}
          className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputCountryTitle")}
              </InputLabel>
              <Autocomplete
                options={countries.map((c) => c.id)}
                getOptionLabel={getOptionLabel("countries")}
                onChange={(e, v) => setInputCountry(v)}
                classes={{ inputRoot: classes.formInputSelect }}
                disabled
                value={inputCountry}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("Register.InputCountryTitle")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} xl={6}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputDepartmentLabel")}
              </InputLabel>
              <Autocomplete
                options={departments.map((d) => d.id)}
                getOptionLabel={getOptionLabel("departments")}
                onChange={(e, v) => setInputDepartment(v)}
                classes={{ inputRoot: classes.formInputSelect }}
                disabled={!inputCountry}
                value={inputDepartment}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("Register.InputDepartmentTitle")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} xl={6}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputCityLabel")}
              </InputLabel>
              <Autocomplete
                options={cities.map((c) => c.id)}
                getOptionLabel={getOptionLabel("cities")}
                onChange={(e, v) => setInputCity(v)}
                classes={{ inputRoot: classes.formInputSelect }}
                disabled={!inputDepartment}
                value={inputCity}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("Register.InputCityTitle")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputStreetLabel")}
              </InputLabel>
              <TextField
                placeholder={i18n.get("Register.InputStreetTitle")}
                className={classes.formInput}
                onChange={(e) => setInputAddressStreet(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressStreet}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} xl={6}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputNumberLabel")}
              </InputLabel>
              <TextField
                placeholder={i18n.get("Register.InputNumberTitle")}
                className={classes.formInput}
                onChange={(e) => setInputAddressNumber(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} xl={6}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputApartmentTitle")}
              </InputLabel>
              <TextField
                placeholder={i18n.get("Register.InputApartmentTitle")}
                className={classes.formInput}
                onChange={(e) => setInputAddressApartment(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressApartment}
                InputProps={{ classes: { input: classes.formInputTrimmed } }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formSelectLabel}>
                {i18n.get("Register.InputCornerLabel")}
              </InputLabel>
              <TextField
                placeholder={i18n.get("Register.InputCornerTitle")}
                className={classes.formInput}
                onChange={(e) => setInputAddressCorner(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressCorner}
              />
            </Grid>
          </Grid>
        </form>
      </div>
      <div
        className={clsx(
          classes.formSubmitContainer,
          classes.formSubmitContainerWithOneButton,
        )}>
        <Button
          className={classes.formSubmitButton}
          onClick={handleSubmit}
          disabled={loading || !canMoveNext}>
          {i18n.get("Register.NextButton")}
        </Button>
      </div>
    </React.Fragment>
  );
};

StepTwo.id = "com.Handy.Register.StepTwo";
export default StepTwo;
